/**
 * Bulk-assign dialog — picks technical assignees from employee options and
 * calls POST /tickets/bulk-assign-tech. Shows success/failed counts from response.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { useEmployeeOptions } from '@/api/employees';
import { useBulkAssignTickets } from '@/api/tickets-workflow';

interface TicketBulkAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketIds: number[];
  onSuccess: () => void;
}

function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

export function TicketBulkAssignDialog({
  open,
  onOpenChange,
  ticketIds,
  onSuccess,
}: TicketBulkAssignDialogProps) {
  const [employeeId, setEmployeeId] = useState<string>('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [result, setResult] = useState<{ success: number; failed: number } | null>(null);

  const { data: employees = [], isLoading: loadingEmployees } = useEmployeeOptions(employeeSearch);
  const mutation = useBulkAssignTickets();

  const close = (next: boolean) => {
    if (mutation.isPending) return;
    if (!next) {
      setEmployeeId('');
      setEmployeeSearch('');
      setResult(null);
      mutation.reset();
    }
    onOpenChange(next);
  };

  const submit = () => {
    const id = parseInt(employeeId, 10);
    if (!id) return;
    mutation.mutate(
      { ticket_ids: ticketIds, technical_assignees: [id] },
      {
        onSuccess: (data) => {
          const counts = data.result ?? { success: 0, failed: 0 };
          setResult(counts);
          onSuccess();
        },
      },
    );
  };

  const canSubmit = !!employeeId && !mutation.isPending && !result;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Bulk assign tickets</DialogTitle>
          <DialogDescription>
            Assign a technical engineer to {ticketIds.length} selected ticket
            {ticketIds.length !== 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {result ? (
              <Alert
                tone={result.failed > 0 ? 'warning' : 'success'}
                title={
                  result.failed > 0
                    ? `Assigned ${result.success}, failed ${result.failed}`
                    : `Assigned ${result.success} ticket${result.success !== 1 ? 's' : ''} successfully`
                }
              />
            ) : null}

            {mutation.isError && !result ? (
              <Alert tone="danger" title={errorMessage(mutation.error)} />
            ) : null}

            {!result && (
              <Field label="Technical assignee" required>
                {({ id }) => (
                  <Select value={employeeId} onValueChange={setEmployeeId}>
                    <SelectTrigger id={id} disabled={loadingEmployees}>
                      <SelectValue placeholder="Select an engineer…" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* value="" trap: never render a SelectItem with value="" */}
                      {employees.map((e) => (
                        <SelectItem
                          key={e.employee_id}
                          value={String(e.employee_id)}
                        >
                          {e.employee_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)} disabled={mutation.isPending}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={!canSubmit} loading={mutation.isPending}>
              Assign
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
